import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { PublicProfileService } from '../../services/PublicProfileService';

function initials(name) {
  const src = (name || '?').trim();
  const parts = src.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

const MEDAL = ['🥇', '🥈', '🥉'];

export default function LeaderboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    PublicProfileService.weeklyLeaderboard(50)
      .then((data) => { if (active) setRows(data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, []);

  const myRank = rows && user ? rows.findIndex((r) => r.uid === user.id) : -1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-violet-50 to-purple-50 px-4 py-10">
      <div className="mx-auto max-w-lg space-y-5">
        <button
          onClick={() => navigate('/progress')}
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition hover:text-slate-700"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          {t('profile.backToHome')}
        </button>

        <div>
          <h1 className="text-2xl font-black text-slate-900">{t('leaderboard.title')}</h1>
          <p className="mt-1 text-sm text-slate-500">{t('leaderboard.subtitle')}</p>
        </div>

        {!isAuthenticated && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {t('leaderboard.guestNotice')}
          </div>
        )}

        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          {rows === null && !error && (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="h-12 animate-pulse rounded-xl bg-slate-100" />
              ))}
            </div>
          )}

          {error && (
            <p className="p-6 text-center text-sm text-slate-500">{t('leaderboard.loadError')}</p>
          )}

          {rows !== null && !error && rows.length === 0 && (
            <p className="p-6 text-center text-sm text-slate-500">{t('leaderboard.empty')}</p>
          )}

          {rows !== null && !error && rows.length > 0 && (
            <ul>
              {rows.map((row, i) => {
                const isMe = user && row.uid === user.id;
                return (
                  <li
                    key={row.uid}
                    className={`flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0 ${
                      isMe ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <span className="w-7 shrink-0 text-center text-sm font-black text-slate-400">
                      {MEDAL[i] || i + 1}
                    </span>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-xs font-black text-white">
                      {initials(row.displayName)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-800">
                      {row.displayName || t('profile.learner')}
                      {isMe && <span className="ml-1.5 text-xs font-semibold text-indigo-500">({t('leaderboard.you')})</span>}
                    </span>
                    {row.streak > 0 && (
                      <span className="shrink-0 text-xs font-bold text-slate-400">🔥 {row.streak}</span>
                    )}
                    <span className="shrink-0 text-sm font-black text-indigo-600">{row.weeklyXp || 0} XP</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {isAuthenticated && rows !== null && !error && myRank === -1 && (
          <p className="text-center text-xs text-slate-400">{t('leaderboard.notRankedYet')}</p>
        )}
      </div>
    </div>
  );
}
